import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, orderBy, query, where, getDoc, doc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { useStudent } from "../hooks/useStudent";
import Layout from "../components/Layout";

const TYPE_ICON = {
  approval:     "🎉",
  fee_approved: "✅",
  fee_rejected: "❌",
  announcement: "📢",
  fee_due:      "💳",
  fee_pending:  "⏳",
  event_due:    "💰",
  new_content:  "🎵",
};

function toYM(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
}
function fmtMonth(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(m,10)-1]} ${y}`;
}
function fmtDueDate(d) {
  if (!d) return "";
  const [y, mo, day] = d.split("-");
  return `${day}/${mo}/${y}`;
}
function fmtDate(iso) {
  if (!iso) return "";
  const d    = new Date(iso);
  const now  = new Date();
  const diffMs   = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) {
    const h = Math.floor(diffMs / 3600000);
    if (h === 0) { const m = Math.floor(diffMs / 60000); return m <= 1 ? "just now" : `${m}m ago`; }
    return `${h}h ago`;
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)  return `${diffDays} days ago`;
  return d.toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" });
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { student, batch, loading: stuLoading } = useStudent();
  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(0);

  useEffect(() => {
    if (!student) return;
    const prevCheck = parseInt(localStorage.getItem(`notifCheck_${student.id}`) || "0");
    setLastCheck(prevCheck);
    localStorage.setItem(`notifCheck_${student.id}`, Date.now().toString());
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  async function load() {
    const uid = auth.currentUser?.uid;
    if (!uid || !student) return;
    try {
      const today     = new Date();
      const currentYM = toYM(today);

      // Fetch everything in parallel
      const [notifSnap, feesSnap, eventFeesSnap, raagSnap] = await Promise.all([
        getDocs(query(collection(db, "notifications"), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, "fees"), where("studentId", "==", student.id))),
        getDocs(query(collection(db, "eventFees"), where("status", "==", "active"))),
        student.courseId
          ? getDocs(collection(db, "courses", student.courseId, "raags"))
          : Promise.resolve(null),
      ]);

      const virtual = [];

      // ── 1. Monthly fee check ──────────────────────────────────────────
      const feeStartYM = student.feeStartDate ? student.feeStartDate.slice(0, 7) : null;
      const feeTrackingStarted = !feeStartYM || feeStartYM <= currentYM;
      const isMonthlyBatch     = !batch?.feeType || batch.feeType === "monthly";

      if (feeTrackingStarted && isMonthlyBatch) {
        const myFees = feesSnap.docs.map(d => d.data());
        const approvedNow = myFees.find(f =>
          f.feeType === "monthly" && f.forMonth === currentYM && f.status === "approved"
        );
        const pendingNow = myFees.find(f =>
          f.feeType === "monthly" && f.forMonth === currentYM && f.status === "pending_approval"
        );

        if (!approvedNow && !pendingNow) {
          // Fee not yet submitted
          virtual.push({
            id:              "virt_fee_due_" + currentYM,
            type:            "fee_due",
            title:           `Monthly Fee Due — ${fmtMonth(currentYM)}`,
            body:            `Your fee for ${fmtMonth(currentYM)} hasn't been submitted yet. Please pay before the 7th.`,
            createdAt:       new Date().toISOString(),
            isActionRequired: true,
            action:          "/fees",
          });
        } else if (pendingNow && !approvedNow) {
          // Submitted, awaiting approval
          virtual.push({
            id:       "virt_fee_pending_" + currentYM,
            type:     "fee_pending",
            title:    `Fee Submitted — Awaiting Approval`,
            body:     `Your payment for ${fmtMonth(currentYM)} has been submitted and is under review.`,
            createdAt: pendingNow.submittedAt || pendingNow.createdAt || new Date().toISOString(),
            isActionRequired: false,
          });
        }
      }

      // ── 2. Event / ad-hoc fee check ───────────────────────────────────
      // A student's current enrolment start — fees created before this date belong
      // to a previous registration and must not follow a re-registered student.
      const enrolledAt = student.registeredAt || student.createdAt || null;

      const relevantEvents = eventFeesSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(ef => {
          // Hide fees that predate this student's current registration
          if (enrolledAt && ef.createdAt && ef.createdAt < enrolledAt) return false;
          // Use assignedStudentIds snapshot when present
          if (ef.assignedStudentIds) return ef.assignedStudentIds.includes(uid);
          // Fallback for legacy event fees — active students only
          if (ef.targetType === "all") return student?.status === "active";
          if (ef.targetType === "batch"   && ef.targetId === student.batchId) return true;
          if (ef.targetType === "student" && ef.targetId === uid) return true;
          return false;
        });

      const eventWithStatus = await Promise.all(
        relevantEvents.map(async ef => {
          const paySnap = await getDoc(doc(db, "eventFees", ef.id, "payments", uid));
          return { ...ef, alreadyPaid: paySnap.exists() };
        })
      );

      eventWithStatus.filter(ef => !ef.alreadyPaid).forEach(ef => {
        const overdue = ef.dueDate && new Date(ef.dueDate + "T23:59:59") < today;
        virtual.push({
          id:               "virt_event_" + ef.id,
          type:             "event_due",
          title:            ef.title,
          body:             `Payment of ₹${Number(ef.amount).toLocaleString("en-IN")} is pending.`
                            + (ef.dueDate ? ` Due: ${fmtDueDate(ef.dueDate)}.` : "")
                            + (overdue ? " Overdue!" : ""),
          createdAt:        ef.createdAt || new Date().toISOString(),
          isActionRequired: true,
          isOverdue:        overdue,
          action:           "/fees",
        });
      });

      // ── 3. New content check ─────────────────────────────────────────
      const contentLastSeen = localStorage.getItem(`contentLastSeen_${student.id}`);
      if (raagSnap && student.courseId) {
        const unlockedIds = batch?.unlockedRaags || [];
        raagSnap.docs.forEach(d => {
          const raag = { id: d.id, ...d.data() };
          if (!unlockedIds.includes(raag.id)) return;
          if (!raag.createdAt) return; // only raags with a timestamp can be "new"
          const isNew = !contentLastSeen || raag.createdAt > contentLastSeen;
          if (isNew) {
            virtual.push({
              id:               "virt_content_" + raag.id,
              type:             "new_content",
              title:            `New Content Added`,
              body:             `"${raag.name}" has been added to your course. Tap to explore.`,
              createdAt:        raag.createdAt,
              isActionRequired: false,
              action:           `/course/${student.courseId}`,
            });
          }
        });
      }

      // ── 4. Admin notifications ────────────────────────────────────────
      // "target: all" notifications only go to active students.
      // Notifications linked to an event fee are hidden when that fee is
      // no longer active or no longer relevant to this student.
      const isActive = student.status === "active";
      const activeEventFeeIds = new Set(relevantEvents.map(ef => ef.id));
      const adminNotifs = notifSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(n => {
          if (n.eventFeeId) return activeEventFeeIds.has(n.eventFeeId);
          if (n.target === "all") {
            if (!isActive) return false;
            // Hide broadcast notifications sent before this student enrolled
            // (covers re-registrations where the UID was reused)
            if (enrolledAt && n.createdAt && n.createdAt < enrolledAt) return false;
            return true;
          }
          return (n.target === "batch"   && n.batchId   === student.batchId) ||
                 (n.target === "student" && n.studentId === student.id);
        });

      // Sort: action-required first, then newest first
      const all = [...virtual, ...adminNotifs].sort((a, b) => {
        if (a.isActionRequired && !b.isActionRequired) return -1;
        if (!a.isActionRequired && b.isActionRequired) return 1;
        return (b.createdAt || "").localeCompare(a.createdAt || "");
      });

      setNotifs(all);
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  const lastCheckIso = new Date(lastCheck).toISOString();

  return (
    <Layout title="🔔 Notifications" student={student}>
      <div style={{ maxWidth:"640px", margin:"0 auto" }}>

        {(loading || stuLoading) ? (
          <div style={{ textAlign:"center", padding:"60px 0", color:"var(--text-muted)" }}>
            Loading…
          </div>
        ) : notifs.length === 0 ? (
          <div style={{ textAlign:"center", padding:"80px 24px", color:"var(--text-muted)", lineHeight:2 }}>
            <div style={{ fontSize:"3rem", marginBottom:"12px" }}>🔔</div>
            <div style={{ fontWeight:"600", fontSize:"1.05rem" }}>All caught up!</div>
            <div style={{ fontSize:".88rem" }}>No pending fees or new updates</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
            {notifs.map(n => {
              const isUnread   = !n.isVirtual && n.createdAt > lastCheckIso;
              const isAction   = n.isActionRequired;
              const isOverdue  = n.isOverdue;
              const isClickable = !!n.action;

              const borderColor = isOverdue   ? "#EF4444"
                                : isAction    ? "#F59E0B"
                                : isUnread    ? "var(--gold)"
                                : "var(--border-warm)";
              const bgColor     = isOverdue   ? "rgba(239,68,68,.07)"
                                : isAction    ? "rgba(245,158,11,.07)"
                                : isUnread    ? "var(--card-bg)"
                                : "var(--input-bg)";

              return (
                <div
                  key={n.id}
                  onClick={isClickable ? () => navigate(n.action) : undefined}
                  style={{
                    display:"flex", gap:"14px", alignItems:"flex-start",
                    padding:"14px 16px",
                    background: bgColor,
                    borderRadius:"12px",
                    border: `1px solid ${borderColor}`,
                    borderLeft: isAction ? `4px solid ${borderColor}` : `1px solid ${borderColor}`,
                    position:"relative",
                    cursor: isClickable ? "pointer" : "default",
                    transition:"opacity .15s",
                  }}
                >
                  {/* Unread dot (admin notifs only) */}
                  {isUnread && !n.isVirtual && (
                    <div style={{
                      position:"absolute", top:"12px", right:"12px",
                      width:"8px", height:"8px", borderRadius:"50%",
                      background:"#EF4444",
                    }}/>
                  )}

                  {/* Action-required badge */}
                  {isAction && (
                    <div style={{
                      position:"absolute", top:"10px", right:"12px",
                      background: isOverdue ? "#EF4444" : "#F59E0B",
                      color:"#fff", fontSize:".68rem", fontWeight:"700",
                      padding:"2px 8px", borderRadius:"50px",
                    }}>
                      {isOverdue ? "OVERDUE" : "ACTION NEEDED"}
                    </div>
                  )}

                  <div style={{ fontSize:"1.6rem", flexShrink:0, lineHeight:1, marginTop:"2px" }}>
                    {TYPE_ICON[n.type] || "📬"}
                  </div>

                  <div style={{ flex:1, minWidth:0, paddingRight: isAction ? "90px" : "0" }}>
                    <div style={{
                      fontWeight: (isUnread || isAction) ? "700" : "600",
                      fontSize:".95rem",
                      color: isOverdue ? "var(--red)" : isAction ? "var(--gold)" : "var(--purple)",
                      marginBottom:"4px",
                    }}>
                      {n.title}
                    </div>
                    <div style={{ fontSize:".88rem", color:"var(--text-label)", lineHeight:1.55 }}>
                      {n.body}
                    </div>
                    <div style={{
                      display:"flex", alignItems:"center", gap:"8px",
                      marginTop:"6px",
                    }}>
                      <span style={{ fontSize:".78rem", color:"var(--text-muted)" }}>
                        {fmtDate(n.createdAt)}
                      </span>
                      {isClickable && (
                        <span style={{
                          fontSize:".75rem", color:"var(--purple-text)",
                          fontWeight:"600",
                        }}>
                          Tap to view →
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ textAlign:"center", marginTop:"28px" }}>
          <button onClick={() => navigate(-1)}
            style={{
              background:"none", border:"1px solid var(--border-warm)",
              color:"var(--text-muted)", borderRadius:"8px",
              padding:"8px 20px", cursor:"pointer", fontSize:".88rem",
            }}>
            ← Back
          </button>
        </div>
      </div>
    </Layout>
  );
}
